/**
 * Investor layout — escape-hatch case for CLAUDE.md's responsive-UI rule.
 *
 * Mobile and desktop are not different *layouts* of the same feature; they
 * are different *interaction models*:
 *
 *   - Desktop: persistent left sidebar (always-visible navigation)
 *   - Mobile: top bar with Burger that opens a slide-in Drawer overlay
 *             with focus trap + auto-close on route change
 *
 * No `@container` rule can express "render this as a modal overlay below
 * 768px and an inline sibling above 768px" — they are structurally
 * different React subtrees with different state machines.
 *
 * This is the ONLY file in the investor app where the
 * `useIsMobile` / `if (isMobile) return ...` pattern is permitted. All
 * other surfaces use container queries.
 */
import ContentLoader from "@funded-here-interface/common/src/components/ContentLoader/ContentLoader";
import { useIsMobile } from "@funded-here-interface/common/src/hooks/useIsMobile";
import { ActionIcon, Burger, Drawer } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBrowser, IconHome, IconSignature, IconCoin, IconX } from "@tabler/icons-react";
import { FC, ReactNode, Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import NavBar from "../../components/NavBar/NavBar";
import { RootState } from "../../store";
import { useStyles } from "./InvestorLayout.styles";

interface IProps {
  content: ReactNode;
}

const InvestorLayout: FC<IProps> = ({ content }) => {
  const isAuthRep = useSelector((state: RootState) => state.auth.isAuthRep);
  const { classes } = useStyles();
  const entityType = useSelector((state: RootState) => state.auth.entityType);
  const isIndividual = entityType === "individual";
  const isMobile = useIsMobile();
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const location = useLocation();

  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  const mainMenus = [
    {
      label: "Dashboard",
      path: "/investor-dashboard/investordashboard",
      icon: <IconHome />,
    },
    {
      label: "Offer Room",
      submenu: [
        { label: "View Offer Room", path: "/note-offer-room" },
        {
          label: "Subscribed/Invested Notes",
          path: "/subscribed-invested-notes",
        },
      ],
      icon: <IconBrowser />,
    },
    ...(isAuthRep || isIndividual
      ? [
          {
            label: "Withdrawal",
            path: "/withdrawal",
            icon: <IconCoin />,
          },
        ]
      : []),
    {
      label: "Signed Documents",
      path: "/investor-signed-documents",
      icon: <IconSignature />,
    },
  ];

  if (isMobile) {
    return (
      <div className={classes.mobileContainer}>
        <header className={classes.mobileTopBar}>
          <Burger
            opened={drawerOpened}
            onClick={toggleDrawer}
            color="white"
            size="sm"
            aria-label="Toggle navigation"
          />
          <span className={classes.mobileTitle}>FundedHere</span>
        </header>
        <Drawer
          opened={drawerOpened}
          onClose={closeDrawer}
          position="left"
          size={260}
          padding={0}
          withCloseButton={false}
          classNames={{ body: classes.drawerBody }}
        >
          <div className={classes.drawerHeader}>
            <ActionIcon onClick={closeDrawer} aria-label="Close navigation" variant="transparent">
              <IconX size={22} color="white" />
            </ActionIcon>
          </div>
          <NavBar menus={mainMenus} embedded />
        </Drawer>
        <main className={classes.mobileHero}>
          <Suspense fallback={<ContentLoader />}>{content}</Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className={classes.mainContainer}>
      <NavBar menus={mainMenus} />
      <div className={classes.hero}>
        <Suspense fallback={<ContentLoader />}>{content}</Suspense>
      </div>
    </div>
  );
};

export default InvestorLayout;
